import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';


import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const TeamTab = () => {
  const { profile, organization } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState({ role: 'member', email: '' });
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [inviteMode, setInviteMode] = useState('email'); // 'email' | 'link'

  const roleOptions = [
    { value: 'admin', label: 'Admin', description: 'Full access to all features and settings' },
    { value: 'member', label: 'Member', description: 'Standard access to CRM features' },
    { value: 'technician', label: 'Technician', description: 'Access to jobs, dispatch, and calendar' },
    { value: 'dispatcher', label: 'Dispatcher', description: 'Access to dispatch board and job management' },
  ];

  const fetchTeamMembers = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('profiles')?.select('*')?.eq('org_id', profile?.org_id)?.order('created_at');
      if (error) throw error;
      setTeamMembers(data || []);
    } catch (err) {
      setErrorMsg(err?.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => { fetchTeamMembers(); }, [fetchTeamMembers]);

  const handleSendEmailInvite = async () => {
    if (!inviteData?.email?.trim()) {
      setErrorMsg('Please enter an email address');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(inviteData?.email?.trim())) {
      setErrorMsg('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/invite-team-member`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          email: inviteData?.email?.trim(),
          role: inviteData?.role,
          org_id: profile?.org_id,
          org_name: organization?.name || '',
          invited_by: profile?.full_name || profile?.email || '',
        }),
      });

      const result = await res?.json();

      if (!res?.ok) {
        throw new Error(result?.error || 'Failed to send invite email');
      }

      setSuccessMsg(`Invite email sent to ${inviteData?.email?.trim()}! They will receive a link to join your team.`);
      setInviteData(prev => ({ ...prev, email: '' }));
      setTimeout(() => setSuccessMsg(''), 10000);
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to send invite email. Please try the link option instead.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setIsLoading(true);
    setErrorMsg('');
    setGeneratedLink('');
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/generate-invite-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          org_id: profile?.org_id,
          role: inviteData?.role,
          invited_by: profile?.id,
          email: inviteData?.email?.trim() || null,
        }),
      });

      const result = await res?.json();

      if (!res?.ok) {
        throw new Error(result?.error || 'Failed to generate invite link');
      }

      const appUrl = window?.location?.origin;
      const link = `${appUrl}/accept-invite?token=${result?.token}`;
      setGeneratedLink(link);
      setSuccessMsg('Invite link generated! Copy and share it with your team member.');
      setTimeout(() => setSuccessMsg(''), 8000);
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to generate invite link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator?.clipboard?.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const el = document?.createElement('textarea');
      el.value = generatedLink;
      document?.body?.appendChild(el);
      el?.select();
      document?.execCommand('copy');
      document?.body?.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleCloseModal = () => {
    setIsInviteModalOpen(false);
    setGeneratedLink('');
    setInviteData({ role: 'member', email: '' });
    setErrorMsg('');
    setSuccessMsg('');
    setInviteMode('email');
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      // Map to ENUM-safe role + extended_role
      const safeRole = newRole === 'admin' ? 'admin' : 'member';
      const extendedRole = ['technician', 'dispatcher'].includes(newRole) ? newRole : null;

      await supabase?.from('profiles')?.update({
        role: safeRole,
        extended_role: extendedRole,
      })?.eq('id', memberId);
      fetchTeamMembers();
    } catch (err) {
      setErrorMsg(err?.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (memberId === profile?.id) {
      setErrorMsg('You cannot remove yourself');
      return;
    }
    if (!window.confirm('Remove this team member?')) return;
    try {
      await supabase?.from('profiles')?.update({ org_id: null })?.eq('id', memberId);
      fetchTeamMembers();
    } catch (err) {
      setErrorMsg(err?.message);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const getEffectiveRole = (member) => {
    return member?.extended_role || member?.role || 'member';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      member: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      technician: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      dispatcher: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-8">
      {successMsg && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">{successMsg}</div>}
      {errorMsg && !isInviteModalOpen && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{errorMsg}</div>}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Icon name="Users" size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">Team Management</h3>
              <p className="text-sm text-muted-foreground">Manage team members, roles, and permissions</p>
            </div>
          </div>
          {profile?.role === 'admin' && (
            <Button variant="default" onClick={() => setIsInviteModalOpen(true)} iconName="UserPlus" iconPosition="left">
              Add User
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Members', value: teamMembers?.length, icon: 'Users', color: 'bg-primary/10 text-primary' },
            { label: 'Admins', value: teamMembers?.filter(m => m?.role === 'admin')?.length, icon: 'Shield', color: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' },
            { label: 'Technicians', value: teamMembers?.filter(m => getEffectiveRole(m) === 'technician')?.length, icon: 'Wrench', color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
            { label: 'Dispatchers', value: teamMembers?.filter(m => getEffectiveRole(m) === 'dispatcher')?.length, icon: 'Truck', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
          ]?.map(stat => (
            <div key={stat?.label} className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat?.color}`}>
                  <Icon name={stat?.icon} size={20} />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-card-foreground">{stat?.value}</p>
                  <p className="text-sm text-muted-foreground">{stat?.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3]?.map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Member</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Joined</th>
                  {profile?.role === 'admin' && (
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {teamMembers?.map((member) => (
                  <tr key={member?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-foreground">{getInitials(member?.full_name)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-card-foreground">{member?.full_name}</p>
                          <p className="text-sm text-muted-foreground">{member?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {profile?.role === 'admin' && member?.id !== profile?.id ? (
                        <select
                          value={getEffectiveRole(member)}
                          onChange={(e) => handleRoleChange(member?.id, e?.target?.value)}
                          className="px-2 py-1 border border-border rounded text-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="technician">Technician</option>
                          <option value="dispatcher">Dispatcher</option>
                        </select>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(getEffectiveRole(member))}`}>
                          {getEffectiveRole(member)}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-sm text-muted-foreground">
                      {member?.created_at ? new Date(member.created_at)?.toLocaleDateString() : '—'}
                    </td>
                    {profile?.role === 'admin' && (
                      <td className="py-4 px-4 text-right">
                        {member?.id !== profile?.id && (
                          <button onClick={() => handleRemoveMember(member?.id)}
                            className="text-error hover:text-error/80 text-sm transition-smooth">
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-elevation-3 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="UserPlus" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground">Invite Team Member</h3>
                  <p className="text-sm text-muted-foreground">Send an invite via email or share a link</p>
                </div>
              </div>
              <button onClick={handleCloseModal} className="text-muted-foreground hover:text-foreground transition-smooth">
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Mode toggle */}
            <div className="flex rounded-lg border border-border overflow-hidden mb-5">
              <button
                onClick={() => { setInviteMode('email'); setGeneratedLink(''); setErrorMsg(''); setSuccessMsg(''); }}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 text-sm font-medium transition-smooth ${inviteMode === 'email' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
              >
                <Icon name="Mail" size={15} />
                <span>Send Email</span>
              </button>
              <button
                onClick={() => { setInviteMode('link'); setSuccessMsg(''); setErrorMsg(''); }}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 text-sm font-medium transition-smooth ${inviteMode === 'link' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
              >
                <Icon name="Link" size={15} />
                <span>Generate Link</span>
              </button>
            </div>

            {errorMsg && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 flex items-center space-x-2">
                <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {successMsg && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400 flex items-center space-x-2">
                <Icon name="CheckCircle" size={14} className="flex-shrink-0" />
                <span>{successMsg}</span>
              </div>
            )}

            <div className="space-y-4">
              {/* Role selector — shared between both modes */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Role</label>
                <select
                  value={inviteData?.role}
                  onChange={(e) => setInviteData(prev => ({ ...prev, role: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {roleOptions?.map(opt => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label} — {opt?.description}</option>
                  ))}
                </select>
              </div>

              {/* EMAIL MODE */}
              {inviteMode === 'email' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Email Address <span className="text-error">*</span>
                    </label>
                    <input
                      type="email"
                      value={inviteData?.email}
                      onChange={(e) => { setInviteData(prev => ({ ...prev, email: e?.target?.value })); setErrorMsg(''); }}
                      placeholder="colleague@example.com"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <Button
                    variant="default"
                    fullWidth
                    onClick={handleSendEmailInvite}
                    disabled={isLoading}
                    iconName={isLoading ? undefined : 'Send'}
                    iconPosition="left"
                  >
                    {isLoading ? (
                      <span className="flex items-center space-x-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending…</span>
                      </span>
                    ) : 'Send Invite Email'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    The user will receive an email with a link to create their account and join your team.
                  </p>
                </>
              )}

              {/* LINK MODE */}
              {inviteMode === 'link' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1">
                      Email (optional)
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">Pre-fill the email field on the signup page</p>
                    <input
                      type="email"
                      value={inviteData?.email}
                      onChange={(e) => setInviteData(prev => ({ ...prev, email: e?.target?.value }))}
                      placeholder="colleague@example.com (optional)"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>

                  {generatedLink ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1 font-medium">Invite Link (valid 7 days)</p>
                        <p className="text-xs text-foreground break-all font-mono leading-relaxed">{generatedLink}</p>
                      </div>
                      <Button
                        variant={copied ? 'outline' : 'default'}
                        fullWidth
                        onClick={handleCopyLink}
                        iconName={copied ? 'Check' : 'Copy'}
                        iconPosition="left"
                      >
                        {copied ? 'Copied!' : 'Copy Link'}
                      </Button>
                      <Button variant="outline" fullWidth onClick={() => setGeneratedLink('')} iconName="RefreshCw" iconPosition="left">
                        Generate New Link
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="default"
                      fullWidth
                      onClick={handleGenerateLink}
                      disabled={isLoading}
                      iconName={isLoading ? undefined : 'Link'}
                      iconPosition="left"
                    >
                      {isLoading ? (
                        <span className="flex items-center space-x-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Generating…</span>
                        </span>
                      ) : 'Generate Invite Link'}
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground text-center">
                    Copy and share this link manually. The employee opens it, creates their account, and joins your organization.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamTab;